import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';

const Overlay = ({ children }) => (
  <motion.div
    className="modal-overlay"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    aria-hidden="true"
  >
    {children}
  </motion.div>
);

Overlay.propTypes = {
  children: PropTypes.node,
};

export default function CardModal({
  layoutId,
  onClose,
  title,
  leftContent,
  rightContent,
}) {
  const root = typeof document !== 'undefined' ? document.body : null;
  const modalRef = useRef(null);

  // lock scroll while modal open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', handler);
    };
  }, [onClose]);

  // autofocus close for keyboard users
  useEffect(() => {
    const btn = modalRef.current?.querySelector('.modal__close');
    if (btn) btn.focus();
  }, []);

  const modal = (
    <Overlay>
      <motion.div
        className="modal-wrapper"
        // shared layoutId for smooth transform from card to modal
        layoutId={layoutId}
        initial={{ scale: 1 }}
        animate={{ scale: 1 }}
        exit={{ scale: 1 }}
        role="dialog"
        aria-modal="true"
        ref={modalRef}
        onClick={(e) => {
          // empêchons la fermeture au clic sur l'overlay : l'utilisateur doit cliquer explicitement sur la croix
          e.stopPropagation();
        }}
      >
        <header className="modal__header">
          <h3 className="modal__title">{title}</h3>
          <button
            className="modal__close"
            aria-label="Fermer"
            onClick={onClose}
            type="button"
          >
            ✕
          </button>
        </header>
        <div className="modal__content">
          <div className="modal__left">{leftContent}</div>

          <div className="modal__divider" aria-hidden="true" />

          <div className="modal__right">{rightContent}</div>
        </div>
      </motion.div>
    </Overlay>
  );

  return root ? createPortal(modal, root) : null;
}

CardModal.propTypes = {
  layoutId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  leftContent: PropTypes.node,
  rightContent: PropTypes.node,
};
